import * as yup from 'yup';

import { Card, CardBody, CardFooter, Form, FormGroup, Input, Label } from 'reactstrap';
import { Institution, ProfessionDto, ProfessionLevel, Program, ProgramAddress, ProgramDto } from '../common/Types';
import React, { useEffect, useState } from 'react';

import Button from './Button';
import { Redirect } from 'react-router-dom';
import { institutionService } from '../services/InstitutionService';
import { professionService } from '../services/ProfessionAdministration';
import { programService } from '../services/ProgramService';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Countries, DegreeType, ProgramStatus, States } from '../common/Data';

interface NewProgramProps {
    onSaved: (newProgram: ProgramDto) => void;
    onCanceled: () => void;
    institutionId: number;
}

const NewProgram = (props: NewProgramProps) => {
    const { onCanceled, institutionId } = props;

    const [navigateToId, setNavigateToId] = useState<number>();
    const [programAddress] = useState<ProgramAddress>();
    const [professions, setProfessions] = useState<ProfessionDto[]>();
    const [levels, setLevels] = useState<ProfessionLevel[]>();
    const [filteredLevels, setFilteredLevels] = useState<ProfessionLevel[]>();
    const [isInternational, setIsInternational] = useState<boolean>(false);
    const [useInstitutionAddress, setUseInstitutionAddress] = useState<boolean>(true);
    const [institution, setInstitution] = useState<Institution>();

    const programAddressSchema = yup
        .object({
            id: yup.number().required().default(0),
            city: yup.string().required('City is required').default(''),
            state: yup.string().required('State is required').default(''),
            country: yup.string().required('Country is required').default('United States of America'),
            address: yup.string().required('Address is required').default(''),
            address2: yup.string().notRequired().nullable().default(null),
            address3: yup.string().notRequired().nullable().default(null),
            address4: yup.string().notRequired().nullable().default(null),
            zip: yup.string().required('Zip code is required').default(''),
            lastEdited: yup.date().notRequired().nullable().default(null),
            dateCreated: yup.date().notRequired().nullable().default(null),
            isDeleted: yup.boolean().required().default(false),
            editedByString: yup.string().notRequired().nullable().default(null),
            createdByString: yup.string().notRequired().nullable().default(null),
        })
        .defined();

    const getProgramSchema = (): yup.ObjectSchema<ProgramDto> =>
        yup
            .object({
                programId: yup.number().required().default(0),
                professionId: yup.number().required('A profession is required').min(1, 'A profession is required').default(0),
                concentrationId: yup.number().notRequired().nullable().default(null),
                institutionId: yup.number().required().default(institutionId),
                title: yup.string().required('Program title is required').default(''),
                programName: yup.string().notRequired().nullable().default(null),
                dateFirstAccredited: yup
                    .date()
                    .notRequired()
                    .nullable()
                    .default(null)
                    .transform((cv, ov) => {
                        return ov === '' ? undefined : cv;
                    }),
                distance: yup.boolean().required().default(false),
                militaryPersonnelOnly: yup.boolean().required().default(false),
                website: yup.string().notRequired().nullable().default(null),
                visible: yup.boolean().required().default(true),
                createdBy: yup.string().notRequired().nullable().default(null),
                dateCreated: yup.date().notRequired().nullable().default(null),
                lastEdited: yup.date().notRequired().nullable().default(null),
                editedBy: yup.string().notRequired().nullable().default(null),
                active: yup.boolean().required().default(true),
                credentilingCode: yup.string().notRequired().nullable().default(null),
                onProbation: yup.boolean().required().default(false),
                probationReason: yup.string().notRequired().nullable().default(null),
                probationStartDate: yup.date().notRequired().nullable().default(null),
                statusId: yup.number().notRequired().nullable().default(null),
                programAddressId: yup.number().required().default(0),
                programAddress: programAddressSchema,
                professionLevelId: yup
                    .number()
                    .nullable()
                    .test(
                        'profession-level-required',
                        'A profession level is required when the profession has levels available',
                        function (value) {
                            const { professionId } = this.parent;
                            const hasLevels = levels?.some((l) => l.professionId === professionId);
                            if (hasLevels && (!value || value < 1)) {
                                return this.createError({
                                    message: 'Please select a profession level',
                                });
                            }
                            return true;
                        },
                    ),
                degreeTypeId: yup.number().notRequired().nullable().default(null),
                activeStatus: yup.number().notRequired().nullable().default(null),
            })
            .defined();

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors, isValid, isSubmitting },
    } = useForm({
        defaultValues: {
            programId: 0,
            professionId: 0,
            concentrationId: null,
            institutionId: institutionId,
            title: '',
            programName: '',
            dateFirstAccredited: null,
            distance: false,
            militaryPersonnelOnly: false,
            website: null,
            visible: true,
            createdBy: null,
            dateCreated: null,
            lastEdited: null,
            editedBy: null,
            active: true,
            credentilingCode: null,
            onProbation: false,
            probationReason: null,
            probationStartDate: null,
            statusId: null,
            programAddressId: 0,
            programAddress: {
                id: 0,
                city: '',
                state: '',
                country: 'United States of America',
                address: '',
                address2: null,
                address3: null,
                address4: null,
                zip: '',
                lastEdited: null,
                dateCreated: null,
                isDeleted: false,
                editedByString: null,
                createdByString: null,
            },
            professionLevelId: null,
            degreeTypeId: null,
            activeStatus: null,
        } as ProgramDto,
        resolver: yupResolver(getProgramSchema() as any),
    });


    const watchProgram = watch();
    const country = watch('programAddress.country');

    useEffect(() => {
        if (country && country !== 'United States of America' && country !== 'Select Country...') {
            setIsInternational(true);
        } else {
            setIsInternational(false);
        }
    }, [country, setValue]);

    useEffect(() => {
        const loadInstitution = () => {
            institutionService
                .getInstitution(institutionId)
                .then((result) => {
                    setInstitution(result);
                })
                .catch(() => {
                    toast.error('Error loading institution');
                });
        };

        const loadProfessions = () => {
            professionService
                .getProfessions()
                .then((results) => {
                    setProfessions([
                        ...results.filter((p) => p.isDeleted === false).sort((a, b) => a.title.localeCompare(b.title)),
                    ]);
                })
                .catch(() => {
                    toast.error('Error loading professions');
                });
        };

        const loadLevels = () => {
            professionService
                .getAllLevels()
                .then((results) => {
                    setLevels([...results]);
                })
                .catch(() => {
                    toast.error('Error loading levels');
                });
        };

        loadInstitution();
        loadProfessions();
        loadLevels();
    }, [institutionId, setProfessions, setLevels]);

    const submitForm = (values: ProgramDto) => {
        // Custom validation for profession level requirement
        const selectedProfessionHasLevels = levels?.some((l) => l.professionId === values.professionId);
        if (selectedProfessionHasLevels && (!values.professionLevelId || values.professionLevelId <= 0)) {
            toast.error('A profession level is required when the selected profession has levels available.');
            return;
        }

        const toastId = toast.info('Creating program...');

        try {
            // Set the program status to Pending and add creation date
            const programData = {
                ...values,
                statusId: ProgramStatus['Pending'],
                activeStatus: ProgramStatus['Pending'],
                dateCreated: new Date()
            };
            
            const programToSave = new Program(programData);
            
            programService
                .saveProgram(programToSave)
                .then((result) => {
                    toast.update(toastId, {
                        render: 'Program created successfully',
                        type: 'success',
                    });

                    setNavigateToId(result.programId);
                })
                .catch((error) => {
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                    toast.update(toastId, {
                        render: `Error creating program: ${errorMessage}`,
                        type: 'error',
                    });
                });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.update(toastId, {
                render: `Error preparing program data: ${errorMessage}`,
                type: 'error',
            });
        }
    };

    const selectedProfession = professions?.find((p) => p.professionId === +(watchProgram.professionId || -1));

    const updateSelectedProfession = (professionId: number) => {
        setFilteredLevels([...(levels?.filter((l) => l.professionId === professionId) || [])]);
        
        // Update the title field when profession changes
        const profession = professions?.find((p) => p.professionId === professionId);
        if (profession) {
            setValue('title', profession.title);
        }
    };

    // Handle "Use Institution Address" checkbox changes
    useEffect(() => {
        if (useInstitutionAddress && institution) {
            setValue('programAddress.address', institution.address || '');
            setValue('programAddress.address2', institution.address2 || '');
            setValue('programAddress.address3', institution.address3 || '');
            setValue('programAddress.address4', institution.address4 || '');
            setValue('programAddress.city', institution.city || '');
            setValue('programAddress.state', institution.state || '');
            setValue('programAddress.zip', institution.zip || '');
            setValue('programAddress.country', institution.country || 'United States of America');
        }
    }, [useInstitutionAddress, institution, setValue]);

    if (navigateToId && navigateToId > 0) {
        return <Redirect to={`/institution/${institutionId}/program/${navigateToId}`} />;
    } else {
        return (
            <>
                <Form noValidate onSubmit={handleSubmit(submitForm)}>
                    <Card className={'mb-0'}>
                        <CardBody>
                            <h4>{`New Program`}</h4>

                            <FormGroup>
                                <Label>{`Profession`}</Label>
                                <Controller
                                    name="professionId"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type={'select'}
                                            {...field}
                                            value={field.value ?? ''}
                                            onChange={(e) => {
                                                field.onChange(e);
                                                updateSelectedProfession(+e.target.value);
                                            }}
                                        >
                                            <option value={-1}>{`Select a Profession`}</option>
                                            {professions?.map((p) => (
                                                <option key={p.professionId} value={p.professionId}>
                                                    {p.title}
                                                </option>
                                            ))}
                                        </Input>
                                    )}
                                />
                                {errors?.professionId?.message && (
                                    <span className={'text-danger'}>{errors.professionId.message}</span>
                                )}
                            </FormGroup>
                            {(filteredLevels?.length || 0) > 0 && (
                                <FormGroup>
                                    <Label>
                                        {`Level`} <span className="text-danger">*</span>
                                    </Label>
                                    <Controller
                                        name="professionLevelId"
                                        control={control}
                                        render={({ field }) => (
                                            <Input type={'select'} {...field} value={field.value ?? ''}>
                                                <option value={-1}>{`Select a Level (Required)`}</option>
                                                {filteredLevels?.map((l) => (
                                                    <option key={l.id} value={l.id}>
                                                        {l.title}
                                                    </option>
                                                ))}
                                            </Input>
                                        )}
                                    />
                                    {errors?.professionLevelId?.message && (
                                        <span className={'text-danger'}>{errors.professionLevelId.message}</span>
                                    )}
                                </FormGroup>
                            )}
                            <Controller
                                name="title"
                                control={control}
                                render={({ field }) => (
                                    <input type={'hidden'} {...field} />
                                )}
                            />
                            <FormGroup>
                                <Label>{`Program Name`}</Label>
                                <Controller
                                    name="programName"
                                    control={control}
                                    render={({ field }) => <Input type={'text'} {...field} value={field.value ?? ''} />}
                                />
                                {errors?.programName?.message && (
                                    <span className={'text-danger'}>{errors.programName.message}</span>
                                )}
                            </FormGroup>
                            <FormGroup>
                                <Label>{`Degree`}</Label>
                                <Controller
                                    name="degreeTypeId"
                                    control={control}
                                    render={({ field }) => (
                                        <Input type={'select'} {...field} value={field.value ?? ''}>
                                            <option value={-1}>{`Select a Degree`}</option>
                                            {Object.keys(DegreeType)
                                                .filter((key) => isNaN(Number(key)))
                                                .map((t) => (
                                                    <option key={t} value={DegreeType[t]}>
                                                        {t}
                                                    </option>
                                                ))}
                                        </Input>
                                    )}
                                />
                                {errors?.degreeTypeId?.message && (
                                    <span className={'text-danger'}>{errors.degreeTypeId.message}</span>
                                )}
                            </FormGroup>
                            <FormGroup>
                                <div className="d-flex align-items-center" style={{ gap: '2rem' }}>
                                    <div className={'custom-control custom-checkbox'}>
                                        <Controller
                                            name="distance"
                                            control={control}
                                            render={({ field: { value, onChange, onBlur, name, ref } }) => (
                                                <Input
                                                    type="checkbox"
                                                    id={`distance`}
                                                    name={name}
                                                    innerRef={ref}
                                                    checked={value ?? false}
                                                    onChange={(e) => onChange(e.target.checked)}
                                                    onBlur={onBlur}
                                                    className={'custom-control-input'}
                                                />
                                            )}
                                        />
                                        <Label for={`distance`} className={'custom-control-label'}>
                                            {`Distance`}
                                        </Label>
                                    </div>

                                    <div className={'custom-control custom-checkbox'}>
                                        <Controller
                                            name="militaryPersonnelOnly"
                                            control={control}
                                            render={({ field: { value, onChange, onBlur, name, ref } }) => (
                                                <Input
                                                    type="checkbox"
                                                    id={`militaryPersonnelOnly`}
                                                    name={name}
                                                    innerRef={ref}
                                                    checked={value ?? false}
                                                    onChange={(e) => onChange(e.target.checked)}
                                                    onBlur={onBlur}
                                                    className={'custom-control-input'}
                                                />
                                            )}
                                        />
                                        <Label for={`militaryPersonnelOnly`} className={'custom-control-label'}>
                                            {`Military Only`}
                                        </Label>
                                    </div>

                                    <div className={'custom-control custom-checkbox'}>
                                        <Controller
                                            name="visible"
                                            control={control}
                                            render={({ field: { value, onChange, onBlur, name, ref } }) => (
                                                <Input
                                                    type="checkbox"
                                                    id={`visible`}
                                                    name={name}
                                                    innerRef={ref}
                                                    checked={value ?? true}
                                                    onChange={(e) => onChange(e.target.checked)}
                                                    onBlur={onBlur}
                                                    className={'custom-control-input'}
                                                />
                                            )}
                                        />
                                        <Label for={`visible`} className={'custom-control-label'}>
                                            {`Visible`}
                                        </Label>
                                    </div>
                                </div>
                            </FormGroup>
                            <FormGroup>
                                <div className={'custom-control custom-checkbox'}>
                                    <Input
                                        type="checkbox"
                                        id={`useInstitutionAddress`}
                                        checked={useInstitutionAddress}
                                        onChange={(e) => setUseInstitutionAddress(e.target.checked)}
                                        className={'custom-control-input'}
                                    />
                                    <Label for={`useInstitutionAddress`} className={'custom-control-label'}>
                                        {`Use Institution Address`}
                                    </Label>
                                </div>
                            </FormGroup>
                            <FormGroup>
                                <Label>{`Address`}</Label>
                                <div>
                                    <Controller
                                        name="programAddress.address"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                id="programAddress.address"
                                                placeholder="Address"
                                                {...field}
                                                value={field.value ?? ''}
                                                disabled={useInstitutionAddress}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="mt-1">
                                    <Controller
                                        name="programAddress.address2"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                id="programAddress.address2"
                                                placeholder="Address 2"
                                                {...field}
                                                value={field.value ?? ''}
                                                disabled={useInstitutionAddress}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="mt-1">
                                    <Controller
                                        name="programAddress.address3"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                id="programAddress.address3"
                                                placeholder="Address 3"
                                                {...field}
                                                value={field.value ?? ''}
                                                disabled={useInstitutionAddress}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="mt-1">
                                    <Controller
                                        name="programAddress.address4"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="text"
                                                id="programAddress.address4"
                                                placeholder="Address 4"
                                                {...field}
                                                value={field.value ?? ''}
                                                disabled={useInstitutionAddress}
                                            />
                                        )}
                                    />
                                </div>
                                {(errors?.programAddress as any)?.address?.message && (
                                    <span className={'text-danger'}>{(errors.programAddress as any).address.message}</span>
                                )}
                                <div className="mt-1 mb-2 d-flex" style={{ gap: '0.5rem' }}>
                                    <div style={{ flex: '1' }}>
                                        <Controller
                                            name="programAddress.city"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="text"
                                                    id="programAddress.city"
                                                    placeholder="City"
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    disabled={useInstitutionAddress}
                                                />
                                            )}
                                        />
                                        {(errors?.programAddress as any)?.city?.message && (
                                            <span className={'text-danger'}>{(errors.programAddress as any).city.message}</span>
                                        )}
                                    </div>
                                    <div style={{ flex: '1' }}>
                                        {!isInternational && (
                                            <Controller
                                                name="programAddress.state"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type={'select'}
                                                        id="programAddress.state"
                                                        placeholder="State"
                                                        {...field}
                                                        value={field.value ?? ''}
                                                        disabled={useInstitutionAddress}
                                                    >
                                                        <option value={undefined}>{`Select State...`}</option>
                                                        {States &&
                                                            States.map((s) => (
                                                                <option key={s.abbreviation} value={s.abbreviation}>
                                                                    {s.abbreviation}
                                                                </option>
                                                            ))}
                                                    </Input>
                                                )}
                                            />
                                        )}
                                        {isInternational && (
                                            <Controller
                                                name="programAddress.state"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type="text"
                                                        id="programAddress.state"
                                                        placeholder="State/Province"
                                                        {...field}
                                                        value={field.value ?? ''}
                                                        disabled={useInstitutionAddress}
                                                    />
                                                )}
                                            />
                                        )}
                                        {(errors?.programAddress as any)?.state?.message && (
                                            <span className={'text-danger'}>{(errors.programAddress as any).state.message}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-1 mb-2 d-flex" style={{ gap: '0.5rem' }}>
                                    <div style={{ flex: '0 0 25%' }}>
                                        <Controller
                                            name="programAddress.zip"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type="text"
                                                    id="programAddress.zip"
                                                    placeholder={isInternational ? `Postal Code` : `Zip`}
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    disabled={useInstitutionAddress}
                                                />
                                            )}
                                        />
                                        {(errors?.programAddress as any)?.zip?.message && (
                                            <span className={'text-danger'}>{(errors.programAddress as any).zip.message}</span>
                                        )}
                                    </div>
                                    <div style={{ flex: '1' }}>
                                        <Controller
                                            name="programAddress.country"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    type={'select'}
                                                    id="programAddress.country"
                                                    placeholder="country"
                                                    {...field}
                                                    value={field.value ?? ''}
                                                    disabled={useInstitutionAddress}
                                                >
                                                    <option value={undefined}>{`Select Country...`}</option>
                                                    {Countries &&
                                                        Countries.map((c) => (
                                                            <option key={c} value={c}>
                                                                {c}
                                                            </option>
                                                        ))}
                                                </Input>
                                            )}
                                        />
                                        {(errors?.programAddress as any)?.country?.message && (
                                            <span className={'text-danger'}>{(errors.programAddress as any).country.message}</span>
                                        )}
                                    </div>
                                </div>
                            </FormGroup>
                        </CardBody>
                        <CardFooter>
                            <Button color={'primary'} type={'submit'}>
                                {`Create Program`}
                            </Button>
                            <Button color={'secondary'} type={'button'} className={'ml-2'} onClick={() => onCanceled()}>
                                {`Cancel`}
                            </Button>
                        </CardFooter>
                    </Card>
                </Form>
            </>
        );
    }
};

export default NewProgram;
